/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe } from "@angular/common";
import { Component, inject } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import {
	combineLatest,
	filter,
	map,
	merge,
	Observable,
	of,
	startWith,
	Subject,
	switchMap
} from "rxjs";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ChartComponent } from "../../../shared/components/chart/chart.component";
import { DataCentersMetricInfoDialogComponent } from "../../../shared/components/data-centers-metric-info-dialog/data-centers-metric-info-dialog.component";
import { DropdownComponent } from "../../../shared/components/dropdown/dropdown.component";
import { KpiComponent } from "../../../shared/components/kpi/kpi.component";
import { MapComponent } from "../../../shared/components/map/map.component";
import { BarChart } from "../../../shared/models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../../../shared/models/http/charts/line-chart/line-chart.model";
import { MapChart } from "../../../shared/models/http/charts/map-chart/map-chart.model";
import { MapRegion } from "../../../shared/models/http/charts/map-chart/map-region.model";
import { MetricDataPoint } from "../../../shared/models/http/data-centers/data-centers-metric-data-point.model";
import { MetricOverviewResponse } from "../../../shared/models/http/data-centers/data-centers-metric-overview-response.model";
import { SiteMetric } from "../../../shared/models/http/data-centers/data-centers-site-metric.model";
import { SiteSerie } from "../../../shared/models/http/data-centers/data-centers-site-serie.model";
import { Site } from "../../../shared/models/http/data-centers/data-centers-site.model";
import { IctCarbonInsightsReportTab } from "../../../shared/models/http/dashboard/ict-carbon-insights-report.model";
import { ChartPattern, ChartResponse } from "../../../shared/models/ui/charts.model";
import { SelectOption } from "../../../shared/models/ui/select-option.model";
import { MaxDecimalsPipe } from "../../../shared/pipes/max-decimals.pipe";
import { AuthorizationService } from "../../../shared/services/authorization.service";
import { DashboardDataCentersService } from "../../../shared/services/dashboard-data-centers.service";
import {
	DataCentersMetricInfoService,
	MetricInfoKey
} from "../../../shared/services/data-centers-metric-info.service";
import { DialogService } from "../../../shared/services/dialog.service";
import { IctCarbonInsightsReportService } from "../../../shared/services/ict-carbon-insights-report.service";
import { COLORS } from "../../../shared/utils/constants";
import { CHART_PATTERNS } from "../../../shared/utils/echarts/chart-constants";
import { GRANTS } from "../../../shared/utils/grants.constants";
import { lazyLoad, Loadable } from "../../../shared/utils/lazy-load";
import { getFiscalYearRange } from "../../../shared/utils/reporting-period";
import { formatDateForBE } from "../../../shared/utils/utils";

type MetricCard<K extends string> = {
	key: K;
	unit: string;
	current: number | null;
	previous: number | null;
	change: number | null;
	annualAverage: number | null;
	chart: ChartResponse<LineChart> | null;
	color: string;
};

@Component({
	selector: "globalictcc-data-centers",
	standalone: true,
	imports: [
		AsyncPipe,
		ReactiveFormsModule,
		MaxDecimalsPipe,
		ChartComponent,
		MapComponent,
		DropdownComponent,
		ButtonComponent,
		KpiComponent
	],
	templateUrl: "./data-centers.component.html",
	styleUrl: "./data-centers.component.scss"
})
export class DataCentersComponent {
	private readonly _dashboardDataCentersService = inject(DashboardDataCentersService);
	private readonly _dialogService = inject(DialogService);
	private readonly _metricInfoService = inject(DataCentersMetricInfoService);
	private readonly _ictCarbonInsightsReportService = inject(IctCarbonInsightsReportService);
	public readonly authorizationService = inject(AuthorizationService);

	public readonly grants = GRANTS.dc;

	public siteOptions: SelectOption[] = [];
	public waterUnitOptions: SelectOption[] = [
		{ label: "litre", value: "litre" },
		{ label: "m³", value: "m³" }
	];
	public emissionUnitOptions: SelectOption[] = [
		{ label: "kg", value: "kgCO2e" },
		{ label: "tonne", value: "tonne" }
	];
	public powerUnitOptions: SelectOption[] = [
		{ label: "kWh", value: "kWh" },
		{ label: "MWh", value: "MWh" },
		{ label: "GWh", value: "GWh" }
	];

	public siteControl = new FormControl<string>("");
	public waterUnitControl = new FormControl<string>(this.waterUnitOptions[0].value);
	public emissionUnitControl = new FormControl<string>(this.emissionUnitOptions[0].value);
	public powerUnitControl = new FormControl<string>(this.powerUnitOptions[0].value);

	public onExportReport(): void {
		const { start, end } = getFiscalYearRange();
		const from = formatDateForBE(start);
		const to = formatDateForBE(end);
		if (!from || !to) return;

		this._ictCarbonInsightsReportService
			.downloadReport(IctCarbonInsightsReportTab.DATA_CENTER, { from, to })
			.subscribe();
	}

	public energyTrendColors: string[] = [
		COLORS.TURQUOISE_100,
		COLORS.GREY_50,
		COLORS.YELLOW_100,
		COLORS.WHITE
	];
	public energyBreakdownColors: string[] = [
		COLORS.TURQUOISE_100,
		COLORS.GREY_100,
		COLORS.GREY_50
	];
	public energyBreakdownPatterns: ChartPattern[] = [
		null,
		CHART_PATTERNS.SQUARES,
		CHART_PATTERNS.DIAGONAL_LINES
	];
	public waterUsageColors: string[] = [COLORS.TURQUOISE_50];

	public energyBreakdownOverrides = {
		series: [{ stack: "total" }, { stack: "total" }, { stack: "total" }],
		yAxis: { axisLabel: { formatter: (value: number) => value.toLocaleString("en-US") } }
	};
	public summaryMapOverrides = {
		title: { show: false },
		visualMap: { show: false }
	};
	public waterUsageOverrides = {
		legend: { show: false },
		grid: { top: 16, left: 0, right: 0, bottom: 0, containLabel: true },
		xAxis: {
			axisLabel: { interval: 0 },
			splitLine: { show: false }
		},
		yAxis: {
			name: "",
			axisLabel: { padding: [0, 0, 0, 0] },
			nameTextStyle: { padding: [0, 0, 0, 0] }
		},
		series: [
			{
				lineStyle: { color: COLORS.TURQUOISE_50 },
				itemStyle: { color: COLORS.TURQUOISE_50 },
				symbol: "rect"
			}
		]
	};
	public energyTrendOverrides = {
		legend: { textStyle: { color: COLORS.GREY_50 } },
		yAxis: { axisLabel: { formatter: (value: number) => value.toLocaleString("en-US") } },
		series: [
			{
				lineStyle: { color: COLORS.TURQUOISE_100 },
				itemStyle: { color: COLORS.TURQUOISE_100 },
				symbol: "rect"
			},
			{
				lineStyle: { color: COLORS.GREY_50 },
				itemStyle: { color: COLORS.GREY_50 },
				symbol: "rect"
			},
			{
				lineStyle: { color: COLORS.YELLOW_100 },
				itemStyle: { color: COLORS.YELLOW_100 },
				symbol: "rect"
			},
			{
				lineStyle: { color: COLORS.WHITE },
				itemStyle: { color: COLORS.WHITE },
				symbol: "rect"
			}
		]
	};
	public efficiencyChartOverrides = {
		legend: { show: false },
		grid: { top: 16, left: 0, right: 0, bottom: 0, containLabel: true },
		xAxis: { axisLabel: { interval: 0 }, splitLine: { show: false } },
		yAxis: {
			name: "",
			splitLine: { show: false },
			axisLabel: { formatter: (value: number) => value.toLocaleString("en-US") }
		},
		series: [
			{
				lineStyle: { color: COLORS.TURQUOISE_100 },
				itemStyle: { color: COLORS.TURQUOISE_100 },
				symbol: "rect"
			}
		]
	};

	private readonly _units$ = merge(
		this.waterUnitControl.valueChanges,
		this.emissionUnitControl.valueChanges,
		this.powerUnitControl.valueChanges
	).pipe(startWith(undefined));
	private readonly _apply$ = new Subject<number>();

	// Streams are cold: an API is only called while a rendered widget subscribes to it.
	private readonly _sites$: Observable<Loadable<SiteSerie>> = lazyLoad(of(true), () =>
		this._dashboardDataCentersService.getUserSiteList()
	);
	private readonly _overview$: Observable<Loadable<MetricOverviewResponse>> = lazyLoad(
		of(true),
		() => this._dashboardDataCentersService.getMetricOverview()
	);

	// The first site is selected once the site list is loaded, then Apply switches site.
	private readonly _selectedSiteId$: Observable<number | null> = this._sites$.pipe(
		filter(sites => !sites.isLoading),
		map(sites => this._syncSelectedSite(sites.data?.sites ?? [])),
		switchMap(siteId => this._apply$.pipe(startWith(siteId)))
	);
	private readonly _siteMetrics$: Observable<Loadable<SiteMetric[]>> = lazyLoad(
		this._selectedSiteId$,
		siteId =>
			siteId === null
				? of<SiteMetric[]>([])
				: this._dashboardDataCentersService.getUserSiteMetricData(siteId)
	);

	public readonly overviewCards$ = this._withUnits(this._overview$, overview =>
		overview ? this._buildOverviewCards(overview) : []
	);
	public readonly summary$ = combineLatest([this._sites$, this._overview$, this._units$]).pipe(
		map(([sites, overview]) => {
			const siteList = sites.data?.sites ?? [];
			const overviewData = overview.data ?? null;
			const hasError = !!sites.hasError || !!overview.hasError;

			return {
				isLoading: sites.isLoading || overview.isLoading,
				mapChart: {
					chart: hasError
						? { mapRegions: [], sitePoints: [] }
						: this._buildMapChart(siteList, overviewData),
					hasError
				} as ChartResponse<MapChart>,
				tableRows: this._buildTableRows(siteList, overviewData)
			};
		})
	);
	public readonly efficiencyCards$ = this._withUnits(this._siteMetrics$, metrics =>
		this._buildEfficiencyCards(metrics ?? [])
	);
	public readonly keyMetricsCards$ = this._withUnits(this._siteMetrics$, metrics =>
		this._buildKeyMetricsCards(metrics ?? [])
	);
	public readonly energyTrendChart$ = this._withUnits(this._siteMetrics$, metrics =>
		this._buildEnergyTrendResponse(metrics ?? [])
	);
	public readonly energyBreakdownChart$ = this._withUnits(this._siteMetrics$, metrics =>
		this._buildEnergyBreakdownResponse(metrics ?? [])
	);

	public onApply() {
		const selectedSiteId = this.siteControl.value;
		if (!selectedSiteId) {
			return;
		}

		this._apply$.next(Number(selectedSiteId));
	}

	public onOpenMetricInfoDialog(key: MetricInfoKey) {
		const metricInfo = this._metricInfoService.getMetricInfoByKey(key);

		this._dialogService.open({
			data: {
				type: "info",
				title: metricInfo.title,
				customComponent: DataCentersMetricInfoDialogComponent,
				customComponentInputs: {
					metricKey: key,
					metricInfo
				},
				closeBtnText: "Close"
			}
		});
	}

	// Re-runs the view builders on every unit change, since they read the unit controls.
	private _withUnits<T, R>(
		source$: Observable<Loadable<T>>,
		build: (data: T | undefined) => R
	): Observable<{ isLoading: boolean; view: R }> {
		return combineLatest([source$, this._units$]).pipe(
			map(([state]) => ({ isLoading: state.isLoading, view: build(state.data) }))
		);
	}

	private _buildEnergyTrendResponse(siteMetrics: SiteMetric[]): ChartResponse<LineChart> {
		const trendMetrics = this._getEnergyTrendMetrics(siteMetrics);

		return {
			chart: trendMetrics.length
				? this._buildEnergyTrendChart(trendMetrics)
				: { lineSeries: [] },
			hasError: !trendMetrics.length
		};
	}

	private _buildEnergyBreakdownResponse(siteMetrics: SiteMetric[]): ChartResponse<BarChart> {
		const breakdownMetrics = this._getBarChartMetrics(siteMetrics);

		return {
			chart: breakdownMetrics.length
				? this._buildBarChart(breakdownMetrics)
				: { barSeries: [] },
			hasError: !breakdownMetrics.length
		};
	}

	private _buildTableRows(sites: Site[], overview: MetricOverviewResponse | null) {
		const overviewMap = new Map((overview?.sites || []).map(site => [site.siteId, site]));

		return sites.map(site => {
			const aggregatedMetrics = overviewMap.get(site.siteId)?.aggregatedMetrics;
			const pue = this._convertEfficiencyValue(
				"pue",
				this._readAggregatedMetric(aggregatedMetrics, "pue")
			);
			const cue = this._convertEfficiencyValue(
				"cue",
				this._readAggregatedMetric(aggregatedMetrics, "cue")
			);
			const wue = this._convertEfficiencyValue(
				"wue",
				this._readAggregatedMetric(aggregatedMetrics, "wue")
			);

			return {
				site: this._getSiteLabel(site),
				pue,
				cue,
				wue,
				pueClass: this._getMetricRangeClass("pue", pue),
				cueClass: this._getMetricRangeClass("cue", cue),
				wueClass: this._getMetricRangeClass("wue", wue)
			};
		});
	}

	private _buildOverviewCards(overview: MetricOverviewResponse | null) {
		return [
			{
				key: "sites" as const,
				value: overview?.sitesCount ?? null,
				unit: ""
			},
			{
				key: "s1s2" as const,
				value: this._convertEmissionValue(overview?.s1s2TotalSites ?? null),
				unit: this.emissionUnitControl.value || "kgCO2e"
			},
			{
				key: "win" as const,
				value: this._convertWaterVolumeValue(overview?.winSites ?? null),
				unit: this._getWaterVolumeUnit()
			}
		];
	}

	private _buildEfficiencyCards(siteMetrics: SiteMetric[]): MetricCard<"pue" | "cue" | "wue">[] {
		if (!siteMetrics.length) {
			return [];
		}

		const definitions = [
			{
				key: "pue" as const,
				metricId: 1,
				color: COLORS.TURQUOISE_100
			},
			{
				key: "cue" as const,
				metricId: 2,
				color: COLORS.GREY_50
			},
			{
				key: "wue" as const,
				metricId: 3,
				color: COLORS.GREY_100
			}
		];

		return definitions.map(definition => {
			const metric = this._findMetricById(siteMetrics, definition.metricId, "key_metric");
			const summary = this._buildSiteMetricSummary(metric, value =>
				this._convertEfficiencyValue(definition.key, value)
			);
			const chart = this._buildEfficiencyChart(definition.key, metric);

			return {
				key: definition.key,
				unit: this._getEfficiencyUnit(definition.key),
				current: summary.current,
				previous: summary.previous,
				change: summary.change,
				annualAverage: summary.annualAverage,
				chart,
				color: definition.color
			};
		});
	}

	public getEfficiencyTitle(key: "pue" | "cue" | "wue"): string {
		switch (key) {
			case "pue":
				return "Power Usage Effectiveness (PUE)";
			case "cue":
				return "Carbon Usage Effectiveness (CUE)";
			case "wue":
				return "Water Usage Effectiveness (WUE)";
			default:
				return "";
		}
	}

	public getOverviewTitle(key: "sites" | "s1s2" | "win"): string {
		switch (key) {
			case "sites":
				return "Sites";
			case "s1s2":
			case "win":
				return this.getKeyMetricsTitle(key);
			default:
				return "";
		}
	}

	public hasOverviewInfo(key: "sites" | "s1s2" | "win"): key is "s1s2" | "win" {
		return key === "s1s2" || key === "win";
	}

	public getKeyMetricsTitle(key: "edc" | "s1s2" | "win"): string {
		switch (key) {
			case "edc":
				return "Energy Consumption (EDC)";
			case "s1s2":
				return "Total Emissions S1 and S2";
			case "win":
				return "Water Usage (WIN)";
			default:
				return "";
		}
	}

	private _getMetricRangeClass(key: "pue" | "cue" | "wue", value: number | null): string {
		if (value === null) {
			return "";
		}
		const ranges = this._metricInfoService.getMetricInfoByKey(key).ranges;
		const index = ranges.findIndex(range =>
			this._metricInfoService.isValueInRange(value, range)
		);
		if (index <= 1) {
			return "metric-value--green";
		}
		if (index === 2) {
			return "metric-value--yellow";
		}
		return "metric-value--red";
	}

	private _buildEfficiencyChart(
		key: "pue" | "cue" | "wue",
		metric: SiteMetric | undefined
	): ChartResponse<LineChart> {
		if (!metric) {
			return { chart: { lineSeries: [] }, hasError: true };
		}

		return {
			chart: {
				lineSeries: [
					{
						name: "",
						linePoints: this._getSortedMetricPoints(metric).map(point => ({
							name: this._formatMonth(point.label),
							value: this._convertEfficiencyValue(key, point.value) ?? 0,
							unit: this._getEfficiencyUnit(key)
						}))
					}
				]
			},
			hasError: false
		};
	}

	private _buildKeyMetricsCards(siteMetrics: SiteMetric[]): MetricCard<"edc" | "s1s2" | "win">[] {
		const definitions = [
			{
				key: "edc" as const,
				metricId: 4,
				color: COLORS.TURQUOISE_100
			},
			{
				key: "s1s2" as const,
				metricId: 5,
				color: COLORS.TURQUOISE_100
			},
			{
				key: "win" as const,
				metricId: 6,
				color: COLORS.TURQUOISE_100
			}
		];

		return definitions.map(definition => {
			const metric = this._findMetricById(siteMetrics, definition.metricId, "key_metric");
			return this._buildKeyMetricCard(definition.key, metric, definition.color);
		});
	}

	private _buildKeyMetricCard(
		key: "edc" | "s1s2" | "win",
		metric: SiteMetric | undefined,
		color: string
	) {
		const summary = this._buildSiteMetricSummary(metric, value => {
			if (!metric) {
				return this._convertOverviewKeyMetricValue(key, value);
			}

			return this._convertMetricValue(metric, value);
		});

		if (!metric) {
			return {
				key,
				unit: this._getOverviewKeyMetricUnit(key),
				current: summary.current,
				previous: summary.previous,
				change: summary.change,
				annualAverage: summary.annualAverage,
				chart: { chart: { lineSeries: [] }, hasError: true },
				color
			};
		}

		const chart = this._buildKeyMetricChart(metric);

		return {
			key,
			unit: this._getMetricDisplayUnit(metric),
			current: summary.current,
			previous: summary.previous,
			change: summary.change,
			annualAverage: summary.annualAverage,
			chart,
			color
		};
	}

	private _findMetricById(
		siteMetrics: SiteMetric[],
		metricId: number,
		widgetType: SiteMetric["widgetType"]
	) {
		return siteMetrics.find(
			metric => metric.metricId === metricId && metric.widgetType === widgetType
		);
	}

	private _buildKeyMetricChart(metric: SiteMetric): ChartResponse<LineChart> {
		return {
			chart: {
				lineSeries: [
					{
						name: "",
						linePoints: this._getSortedMetricPoints(metric).map(point => ({
							name: this._formatMonth(point.label),
							value: this._convertMetricValue(metric, point.value) ?? 0,
							unit: this._getMetricDisplayUnit(metric)
						}))
					}
				]
			},
			hasError: false
		};
	}

	private _findKeyMetric(siteMetrics: SiteMetric[], keywords: string[]) {
		const lowerKeywords = keywords.map(keyword => keyword.toLowerCase());
		return siteMetrics.find(metric =>
			lowerKeywords.some(keyword => metric.metrics.metricName.toLowerCase().includes(keyword))
		);
	}

	private _buildSiteOptions(sites: Site[]): SelectOption[] {
		return sites.map(site => ({
			label: this._getSiteLabel(site),
			value: String(site.siteId)
		}));
	}

	private _syncSelectedSite(sites: Site[]): number | null {
		this.siteOptions = this._buildSiteOptions(sites);

		const currentSelection = this.siteControl.value;
		const selectedSite =
			sites.find(site => String(site.siteId) === currentSelection) ?? sites[0];
		this.siteControl.setValue(selectedSite ? String(selectedSite.siteId) : "", {
			emitEvent: false
		});

		return selectedSite?.siteId ?? null;
	}

	private _readAggregatedMetric(
		aggregatedMetrics: Record<string, { aa?: number; yt?: number; ytd?: number }> | undefined,
		key: string
	): number | null {
		if (!aggregatedMetrics || !aggregatedMetrics[key]) {
			return null;
		}

		const metric = aggregatedMetrics[key];
		// return metric.ytd ?? metric.yt ?? metric.aa ?? null;
		return metric.yt ?? null;
	}

	private _buildSiteMetricSummary(
		metric: SiteMetric | undefined,
		convertValue: (value: number | null | undefined) => number | null
	) {
		if (!metric) {
			return {
				current: 0,
				previous: 0,
				change: 0,
				annualAverage: 0
			};
		}

		const points = this._getSortedMetricPoints(metric);
		const currentPoint = points.length ? points[points.length - 1].value : null;
		const previousPoint = points.length > 1 ? points[points.length - 2].value : null;
		const current = convertValue(currentPoint) ?? 0;
		const previous = convertValue(previousPoint) ?? 0;
		const annualAverage = convertValue(this._parseNumber(metric.metrics.annualAvg)) ?? 0;

		return {
			current,
			previous,
			change: previous !== 0 ? ((current - previous) / previous) * 100 : 0,
			annualAverage
		};
	}

	private _buildMapChart(sites: Site[], overview: MetricOverviewResponse | null): MapChart {
		const overviewMap = new Map((overview?.sites || []).map(site => [site.siteId, site]));
		const regionValues = new Map<string, number>();

		for (const site of sites) {
			const regionName = site.regionName || this._getSiteLabel(site);
			const fallbackValue = 1;
			const metricValues = Object.values(
				overviewMap.get(site.siteId)?.aggregatedMetrics || {}
			);
			const regionValue = metricValues[0]?.ytd ?? fallbackValue;
			regionValues.set(regionName, (regionValues.get(regionName) || 0) + regionValue);
		}

		const mapRegions: MapRegion[] = Array.from(regionValues.entries()).map(([name, value]) => ({
			name,
			shortName: this._getShortName(name),
			value,
			unit: "sites"
		}));

		const sitePoints = sites
			.filter(
				site =>
					this._isCoordinateValid(site.longitude) &&
					this._isCoordinateValid(site.latitude)
			)
			.map(site => ({
				name: this._getSiteLabel(site),
				value: [Number(site.longitude), Number(site.latitude)] as [number, number]
			}));

		return { mapRegions, sitePoints };
	}

	private _buildEnergyTrendChart(metrics: SiteMetric[]): LineChart {
		const trendMetricNamesById = new Map<number, string>([
			[7, "Energy Consumption (EDC)"],
			[8, "Renewable Energy from Guarantees of Origin (ERES-GOO)"],
			[5, "Total Carbon Emissions (Scope 1 + Scope 2)"],
			[9, "Location-Based Emissions (LBE)"]
		]);

		return {
			lineSeries: metrics.map(metric => ({
				name: trendMetricNamesById.get(metric.metricId) || metric.metrics.metricName,
				linePoints: this._getSortedMetricPoints(metric).map(point => ({
					name: this._formatMonth(point.label),
					value: this._convertMetricValue(metric, point.value) ?? 0,
					unit: this._getMetricDisplayUnit(metric)
				}))
			}))
		};
	}

	private _buildBarChart(metrics: SiteMetric[], title?: string): BarChart {
		const sortedReferencePoints = metrics[0] ? this._getSortedMetricPoints(metrics[0]) : [];
		const categories = sortedReferencePoints.map(point => ({
			key: point.label,
			name: this._formatMonth(point.label)
		}));

		return {
			title: title || undefined,
			barSeries: metrics.map(metric => {
				const sortedPointsByLabel = new Map(
					this._getSortedMetricPoints(metric).map(point => [point.label, point.value])
				);

				return {
					name: metric.metrics.metricName,
					bars: categories.map(category => ({
						name: category.name,
						value:
							this._convertMetricValue(
								metric,
								sortedPointsByLabel.get(category.key) || 0
							) || 0,
						unit: this._getMetricDisplayUnit(metric)
					}))
				};
			})
		};
	}

	private _getSortedMetricPoints(metric: SiteMetric | undefined): MetricDataPoint[] {
		if (!metric?.metrics?.data?.length) {
			return [];
		}

		return [...metric.metrics.data].sort((firstPoint, secondPoint) => {
			const firstValue = this._getMetricPointSortValue(firstPoint.label);
			const secondValue = this._getMetricPointSortValue(secondPoint.label);

			if (firstValue !== secondValue) {
				return firstValue - secondValue;
			}

			return String(firstPoint.label).localeCompare(String(secondPoint.label));
		});
	}

	private _getMetricPointSortValue(label: string): number {
		const normalizedLabel = String(label || "").trim();

		const isoLikeMatch = normalizedLabel.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/);
		if (isoLikeMatch) {
			const year = Number(isoLikeMatch[1]);
			const monthIndex = Number(isoLikeMatch[2]) - 1;
			const day = Number(isoLikeMatch[3]);
			return Date.UTC(year, monthIndex, day);
		}

		const parsedDate = Date.parse(normalizedLabel);
		if (!Number.isNaN(parsedDate)) {
			return parsedDate;
		}

		return Number.MAX_SAFE_INTEGER;
	}

	private _convertMetricValue(
		metric: SiteMetric,
		value: number | null | undefined
	): number | null {
		if (value === null || value === undefined) {
			return null;
		}

		const metricName = metric.metrics.metricName.toLowerCase();

		if (
			metricName.includes("emission") ||
			metricName.includes("co2") ||
			metricName.includes("scope 1") ||
			metricName.includes("scope 2")
		) {
			return this._convertEmissionValue(value);
		}

		if (metricName.includes("water") || metricName.includes("win")) {
			return this._convertWaterValue(metric, value);
		}

		if (metricName.includes("energy") || metricName.includes("edc")) {
			return this._convertEnergyValue(value);
		}

		if (metricName.includes("pue") || metricName.includes("power usage effectiveness")) {
			return value;
		}

		return value;
	}

	private _convertEfficiencyValue(
		key: "pue" | "cue" | "wue",
		value: number | null | undefined
	): number | null {
		if (value === null || value === undefined) {
			return null;
		}

		switch (key) {
			case "cue":
				return this._convertEmissionEfficiencyValue(value);
			case "wue":
				return this._convertWaterEfficiencyValue(value);
			case "pue":
			default:
				return value;
		}
	}

	private _convertEmissionValue(value: number | null | undefined): number | null {
		if (value === null || value === undefined) {
			return null;
		}

		return value * this._getEmissionUnitFactor();
	}

	private _convertWaterValue(
		metric: SiteMetric,
		value: number | null | undefined
	): number | null {
		if (value === null || value === undefined) {
			return null;
		}

		const metricName = metric.metrics.metricName.toLowerCase();

		if (metricName.includes("effectiveness") || metricName.includes("wue")) {
			return this._convertWaterEfficiencyValue(value);
		}

		return this._convertWaterVolumeValue(value);
	}

	private _convertWaterVolumeValue(value: number | null | undefined): number | null {
		if (value === null || value === undefined) {
			return null;
		}

		return value * this._getWaterVolumeFactor();
	}

	private _convertWaterEfficiencyValue(value: number | null | undefined): number | null {
		if (value === null || value === undefined) {
			return null;
		}

		return value * this._getWaterEfficiencyFactor();
	}

	private _convertEmissionEfficiencyValue(value: number | null | undefined): number | null {
		if (value === null || value === undefined) {
			return null;
		}

		return value * this._getEmissionEfficiencyFactor();
	}

	private _convertEnergyValue(value: number | null | undefined): number | null {
		if (value === null || value === undefined) {
			return null;
		}

		return value * this._getPowerUnitFactor();
	}

	private _convertOverviewKeyMetricValue(
		key: "edc" | "s1s2" | "win",
		value: number | null | undefined
	): number | null {
		switch (key) {
			case "s1s2":
				return this._convertEmissionValue(value);
			case "win":
				return this._convertWaterVolumeValue(value);
			case "edc":
			default:
				return this._convertEnergyValue(value);
		}
	}

	private _getMetricDisplayUnit(metric: SiteMetric): string {
		const metricName = metric.metrics.metricName.toLowerCase();

		if (
			metricName.includes("emission") ||
			metricName.includes("co2") ||
			metricName.includes("scope 1") ||
			metricName.includes("scope 2")
		) {
			return this.emissionUnitControl.value || metric.metrics.Unit || "";
		}

		if (metricName.includes("water") || metricName.includes("win")) {
			if (metricName.includes("effectiveness") || metricName.includes("wue")) {
				return this._getWaterEfficiencyUnit();
			}

			return this._getWaterVolumeUnit();
		}

		if (metricName.includes("energy") || metricName.includes("edc")) {
			return this.powerUnitControl.value || metric.metrics.Unit || "";
		}

		if (metricName.includes("pue") || metricName.includes("power usage effectiveness")) {
			return "";
		}

		return metric.metrics.Unit || "";
	}

	private _getEfficiencyUnit(key: "pue" | "cue" | "wue"): string {
		switch (key) {
			// case "cue":
			// 	return this._getEmissionEfficiencyUnit();
			// case "wue":
			// 	return this._getWaterEfficiencyUnit();
			// case "pue":
			default:
				return "";
		}
		// Issue #1477 --> Remove the unit for efficiency metrics for cue and wue as per the latest decision.
		return "";
	}

	private _getOverviewKeyMetricUnit(key: "edc" | "s1s2" | "win"): string {
		switch (key) {
			case "s1s2":
				return this.emissionUnitControl.value || "kgCO2e";
			case "win":
				return this._getWaterVolumeUnit();
			case "edc":
			default:
				return this.powerUnitControl.value || "kWh";
		}
	}

	private _getWaterVolumeUnit(): string {
		return this.waterUnitControl.value || "litre";
	}

	private _getWaterEfficiencyUnit(): string {
		return `${this._getWaterVolumeUnit()}/${this.powerUnitControl.value || "kWh"}`;
	}

	private _getEmissionEfficiencyUnit(): string {
		return `${this.emissionUnitControl.value || "kg"}/${this.powerUnitControl.value || "kWh"}`;
	}

	private _getEmissionUnitFactor(): number {
		return this.emissionUnitControl.value === "tonne" ? 1 / 1000 : 1;
	}

	private _getWaterVolumeFactor(): number {
		return this.waterUnitControl.value === "litre" ? 1000 : 1;
	}

	private _getPowerUnitFactor(): number {
		switch (this.powerUnitControl.value) {
			case "MWh":
				return 1 / 1000;
			case "GWh":
				return 1 / 1000000;
			case "kWh":
			default:
				return 1;
		}
	}

	private _getPowerRatioFactor(): number {
		switch (this.powerUnitControl.value) {
			case "MWh":
				return 1000;
			case "GWh":
				return 1000000;
			case "kWh":
			default:
				return 1;
		}
	}

	private _getWaterEfficiencyFactor(): number {
		return (this._getWaterVolumeFactor() * this._getPowerRatioFactor()) / 1000;
	}

	private _getEmissionEfficiencyFactor(): number {
		return this._getEmissionUnitFactor() * this._getPowerRatioFactor();
	}

	private _getPointChartMetrics(siteMetrics: SiteMetric[]) {
		const pointMetrics = siteMetrics
			.filter(metric => metric.widgetType === "point_chart")
			.sort((firstMetric, secondMetric) => firstMetric.metricId - secondMetric.metricId);

		return pointMetrics.length ? pointMetrics : siteMetrics.slice(0, 3);
	}

	private _getEnergyTrendMetrics(siteMetrics: SiteMetric[]) {
		const pointMetricsById = new Map(
			siteMetrics
				.filter(metric => metric.widgetType === "point_chart")
				.map(metric => [metric.metricId, metric])
		);

		const edcMetric =
			pointMetricsById.get(7) ||
			this._findMetricById(siteMetrics, 4, "key_metric") ||
			this._findKeyMetric(siteMetrics, ["energy", "edc"]);
		const eresGooMetric =
			pointMetricsById.get(8) ||
			this._findKeyMetric(siteMetrics, ["renewable", "eres", "goo"]);
		const totalEmissionMetric =
			this._findMetricById(siteMetrics, 5, "key_metric") ||
			this._findKeyMetric(siteMetrics, ["total emission", "scope 1 + scope 2"]);
		const lbeMetric =
			pointMetricsById.get(9) ||
			this._findKeyMetric(siteMetrics, ["location based", "lbe", "scope 2"]);

		return [edcMetric, eresGooMetric, totalEmissionMetric, lbeMetric].filter(
			(metric): metric is SiteMetric => Boolean(metric)
		);
	}

	private _getBarChartMetrics(siteMetrics: SiteMetric[]) {
		const barMetrics = siteMetrics
			.filter(metric => metric.widgetType === "bar_chart")
			.sort((firstMetric, secondMetric) => firstMetric.metricId - secondMetric.metricId);

		return barMetrics.length ? barMetrics : siteMetrics.slice(0, 3);
	}

	private _getSiteLabel(site: Site): string {
		return site.siteName || String(site.siteId);
	}

	private _getShortName(value: string): string {
		const words = value.split(/\s+/).filter(Boolean);
		if (!words.length) {
			return value.slice(0, 2).toUpperCase();
		}

		return words
			.slice(0, 2)
			.map(word => word[0]?.toUpperCase() || "")
			.join("");
	}

	private _formatMonth(dateString: string): string {
		const months = [
			"JAN",
			"FEB",
			"MAR",
			"APR",
			"MAY",
			"JUN",
			"JUL",
			"AUG",
			"SEP",
			"OCT",
			"NOV",
			"DEC"
		];
		const normalizedDateString = String(dateString || "").trim();

		// Parse ISO-like dates directly from the raw string to avoid timezone-based month shifts.
		const isoMonthMatch = normalizedDateString.match(
			/^(?:\d{4})-(\d{1,2})-(?:\d{1,2})(?:[T\s].*)?$/
		);
		if (isoMonthMatch) {
			const monthIndex = Number(isoMonthMatch[1]) - 1;

			if (monthIndex >= 0 && monthIndex < months.length) {
				return months[monthIndex];
			}
		}

		const date = new Date(normalizedDateString);
		if (Number.isNaN(date.getTime())) {
			return normalizedDateString;
		}

		return months[date.getMonth()];
	}

	private _parseNumber(value: string | number | undefined): number {
		if (value === undefined || value === null) {
			return 0;
		}

		const parsed = typeof value === "number" ? value : Number(value);
		return Number.isNaN(parsed) ? 0 : parsed;
	}

	private _isCoordinateValid(value: string | undefined): boolean {
		if (!value) {
			return false;
		}

		return !Number.isNaN(Number(value));
	}
}
